import * as is from 'utilities/is';
import { allFound } from 'utilities/list';
import { POINT_ALLOCATION_RULES, ANSWER_TYPES } from 'constants/annotations';

export const GradeCalculatorFactory = {
    create(config = {}) {
        if (config.evaluateQuiz === false) {
            return new NullGradingCalculator(config);
        }

        return new GradeCalculator(config);
    }
};

export class GradeCalculator {
    constructor({
        passingThreshold
    }) {
        this.passingThreshold = passingThreshold;
        this.answers = {};
        this.questions = [];
    }

    set answerKey(questions = []) {
        this.questions = questions;
        this.correctAnswers = questions.reduce((correct, q) => {
            correct[q.id] = {
                points: q.properties?.points || 0,
                answers: (
                    q.descendants?.filter(d => d.type === 'answer' && d.properties?.correct)
                        .map(a => {
                            if (is.defined(a.properties?.value)) {
                                return a.properties.value;
                            } else if (is.defined(a.properties?.min)) {
                                return a.properties.min;
                            }

                            return a.content;
                        }) || []
                ),
                questionType: q.properties?.questionType,
                gradingRule: q.properties?.gradingRule 
            }

            return correct;
        }, {});

        delete this._answerEvaluation;
    }

    set currentAnswers(answers) {
        this.answers = answers;
        delete this._answerEvaluation;
    }

    get unansweredQuestions() {
        if (is.empty(this.answers)) {
            return this.questions.map((q,i) => i);
        }

        return (
            this.questions
                .filter(({id}) => !(id in this.answers) || this.answers[id] === '')
                .map(q => this.questions.findIndex(qq => qq.id === q.id))
        )
    }

    get answeredQuestions() {
        return (
            this.questions
                .filter(q => q.id in this.answers && this.answers[q.id] !== '')
                .map(q => this.questions.findIndex(qq => qq.id === q.id))
        )
    }

    get answeredCorrectly() {
        return this.answerEvaluation.answeredCorrectly;
    }

    get answeredIncorrectly() {
        return this.answerEvaluation.answeredIncorrectly;
    }

    get answerEvaluation() {
        if (!this._answerEvaluation) {
            this._answerEvaluation = (
                Object
                    .keys(this.correctAnswers)
                    .reduce((evaluation, questionId) => {
                        // only evaluate questions where an answer has been provided
                        if (this.answers[questionId]) {
                            const {
                                answers: correctAnswer,
                                questionType,
                                gradingRule = POINT_ALLOCATION_RULES.ANY_CORRECT
                            }  = this.correctAnswers[questionId];
    
                            switch(questionType) {
                                case ANSWER_TYPES.MULTIPLE_CHOICE:
                                case ANSWER_TYPES.BOOLEAN:
                                    if (correctAnswer.includes(this.answers[questionId])) {
                                        evaluation.answeredCorrectly.push(questionId);
                                    } else {
                                        evaluation.answeredIncorrectly.push(questionId);
                                    }
                                    break;
                                
                                case ANSWER_TYPES.CHECKBOX:
                                    let correct = false;
    
                                    if (gradingRule === POINT_ALLOCATION_RULES.ANY_CORRECT) {
                                        correct = allFound(correctAnswer, this.answers[questionId]);
                                    } else if (gradingRule === POINT_ALLOCATION_RULES.ALL_CORRECT) {
                                        correct = is.equal(correctAnswer.sort(), this.answers[questionId]?.sort());
                                    }
    
                                    if (correct) {
                                        evaluation.answeredCorrectly.push(questionId);
                                    } else {
                                        evaluation.answeredIncorrectly.push(questionId);
                                    }
                                    
                                    break;
    
                                case ANSWER_TYPES.ANNOTATION:
                                case ANSWER_TYPES.LINEAR_SCALE:
                                    // Annotation and Linear Scale annotation type scoring not supported yet
                                    // if (correctAnswer === this.answers[questionId]) {
                                    //     evaluation.answeredCorrectly.push(questionId);
                                    // } else {
                                    //     evaluation.answeredIncorrectly.push(questionId);
                                    // }
                                    break;
    
                                case ANSWER_TYPES.OPEN_RESPONSE:
                                    if (this.answers[questionId].length >= correctAnswer[0]) {
                                        evaluation.answeredCorrectly.push(questionId);
                                    } else {
                                        evaluation.answeredIncorrectly.push(questionId);
                                    }
                                    break;
                            }
                        }

                        return evaluation;
                    }, { answeredCorrectly: [], answeredIncorrectly: [] })
            )
        }

        return this._answerEvaluation;
    }

    get score() {
        return (
            this.answeredCorrectly
                .reduce((score, key) => (
                    score + this.correctAnswers[key].points
                ), 0)
        )
    }

    get passing() {
        return this.score >= this.passingThreshold;
    }
}

export class NullGradingCalculator extends GradeCalculator {
    constructor(...args) {
        super(...args);

        this._answerEvaluation = {
            answeredCorrectly: [],
            answeredIncorrectly: []
        }
    }

    set answerKey(questions) {}
    set currentAnswers(answers) {}

    get answerEvaluation() {
        return this._answerEvaluation;
    }

    get passing() {
        return null;
    }
}